// components/SelfPaced/StudentEnrollmentsPage.js
// "My Learning" — dashboard of enrolled self-paced courses
// Themed, responsive, light/dark mode via CSS variables
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import {
  BookOpen, Clock, Play, CheckCircle2, Search,
  GraduationCap, Trophy, BarChart3, LayoutGrid, List,
  Award, AlertTriangle, CalendarClock, ShoppingCart,
} from 'lucide-react';
import { AppButton, AppBadge, EmptyState, ProgressBar, PageHeader, SearchInput, FilterBar, FilterSelect } from '../ui';
import { getMyEnrollments, getCourseProgress } from '../../utils/selfPacedApi';
import './_self-paced-pages.scss';

const STATUS_OPTIONS = [
  { value: '', label: 'All Courses' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'not-started', label: 'Not Started' },
  { value: 'expired', label: 'Expired' },
];

/* ── Enrollment Card ───────────────────────────────────────── */
const EnrollmentCard = ({ enrollment, onClick, onViewCert, onRepurchase }) => {
  const course = enrollment.course || enrollment;
  const pct = enrollment.progress ?? enrollment.percentage ?? 0;
  const isComplete = pct >= 100;
  const isStarted = pct > 0;
  const isExpired = enrollment.status === 'expired' ||
    (enrollment.expiresAt && new Date(enrollment.expiresAt) < new Date());

  // Calculate days remaining
  let daysRemaining = null;
  if (enrollment.expiresAt && !isExpired) {
    daysRemaining = Math.max(0, Math.ceil((new Date(enrollment.expiresAt) - new Date()) / (1000 * 60 * 60 * 24)));
  }

  return (
    <div className={`spe-card ${isExpired ? 'spe-card--expired' : ''}`} onClick={isExpired ? undefined : onClick} role="button" tabIndex={0}>
      <div className="spe-card__thumb">
        {(course.thumbnailUrl || course.thumbnail) ? (
          <img src={course.thumbnailUrl || course.thumbnail} alt={course.title || course.name} />
        ) : (
          <div className="spe-card__thumb-placeholder"><BookOpen size={28} /></div>
        )}
        {isExpired && <div className="spe-card__expired-overlay"><AlertTriangle size={22} /></div>}
      </div>

      <div className="spe-card__body">
        <h4 className="spe-card__title">{course.title || course.name}</h4>
        <div className="spe-card__meta">
          {course.level && <span><BarChart3 size={12} /> {course.level}</span>}
          {course.duration && <span><Clock size={12} /> {course.duration}</span>}
          {daysRemaining !== null && daysRemaining <= 7 && (
            <span className="spe-card__expiry-warn"><CalendarClock size={12} /> {daysRemaining}d left</span>
          )}
        </div>
        {!isExpired && (
          <div className="spe-card__progress">
            <ProgressBar value={pct} color={isComplete ? 'emerald' : 'blue'} size="sm" />
            <span className="spe-card__pct">{Math.round(pct)}%</span>
          </div>
        )}
        {isExpired && (
          <div className="spe-card__expired-msg">
            <AlertTriangle size={14} />
            <span>Your course access has expired</span>
          </div>
        )}
      </div>

      <div className="spe-card__footer">
        {isExpired ? (
          <AppButton size="sm" variant="primary" onClick={(e) => { e.stopPropagation(); onRepurchase(course); }}>
            <ShoppingCart size={14} /> Repurchase Course
          </AppButton>
        ) : isComplete ? (
          <div className="spe-card__footer-row">
            <AppBadge color="emerald"><CheckCircle2 size={13} /> Completed</AppBadge>
            <AppButton size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onViewCert(course._id || course); }}>
              <Award size={14} /> Certificate
            </AppButton>
          </div>
        ) : (
          <AppButton size="sm" variant={isStarted ? 'primary' : 'outline'}>
            <Play size={14} /> {isStarted ? 'Continue' : 'Start Learning'}
          </AppButton>
        )}
      </div>
    </div>
  );
};

/* ── Main Page ──────────────────────────────────────────────── */
export default function StudentEnrollmentsPage() {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await getMyEnrollments();
      const list = res.data?.data || res.data || [];

      // Optionally enrich with progress for each enrollment
      const enriched = await Promise.all(
        list.map(async (e) => {
          const courseId = e.course?._id || e.course;
          if (!courseId) return e;
          try {
            const prog = await getCourseProgress(courseId);
            const p = prog.data?.data || prog.data;
            return { ...e, progress: p?.percentage ?? p?.progress ?? 0 };
          } catch {
            return { ...e, progress: 0 };
          }
        })
      );
      setEnrollments(enriched);
    } catch {
      setEnrollments([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let list = [...enrollments];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(e => {
        const c = e.course || e;
        return (c.title || c.name || '').toLowerCase().includes(q);
      });
    }
    if (status === 'completed') list = list.filter(e => (e.progress ?? 0) >= 100 && e.status !== 'expired');
    if (status === 'in-progress') list = list.filter(e => (e.progress ?? 0) > 0 && (e.progress ?? 0) < 100 && e.status !== 'expired');
    if (status === 'not-started') list = list.filter(e => (e.progress ?? 0) === 0 && e.status !== 'expired');
    if (status === 'expired') list = list.filter(e => e.status === 'expired' || (e.expiresAt && new Date(e.expiresAt) < new Date()));
    return list;
  }, [enrollments, search, status]);

  const stats = useMemo(() => {
    const total = enrollments.length;
    const completed = enrollments.filter(e => (e.progress ?? 0) >= 100 && e.status !== 'expired').length;
    const inProgress = enrollments.filter(e => (e.progress ?? 0) > 0 && (e.progress ?? 0) < 100 && e.status !== 'expired').length;
    const expired = enrollments.filter(e => e.status === 'expired' || (e.expiresAt && new Date(e.expiresAt) < new Date())).length;
    return { total, completed, inProgress, expired };
  }, [enrollments]);

  return (
    <div className="sp-page">
      <PageHeader
        title="My Learning"
        subtitle={`${stats.total} enrolled course${stats.total !== 1 ? 's' : ''}`}
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <AppBadge color="blue"><BookOpen size={12} /> {stats.inProgress} In Progress</AppBadge>
            <AppBadge color="emerald"><Trophy size={12} /> {stats.completed} Completed</AppBadge>
            {stats.expired > 0 && <AppBadge color="red"><AlertTriangle size={12} /> {stats.expired} Expired</AppBadge>}
          </div>
        }
      />

      <FilterBar
        actions={<SearchInput placeholder="Search your courses..." value={search} onChange={setSearch} />}
      >
        <FilterSelect value={status} onChange={setStatus} options={STATUS_OPTIONS} />
        <AppBadge variant="secondary">{filtered.length} course{filtered.length !== 1 ? 's' : ''}</AppBadge>
      </FilterBar>

      {loading ? (
        <div className="sp-loading"><Spinner animation="border" variant="secondary" /></div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<GraduationCap size={28} />}
          title={enrollments.length === 0 ? 'No enrollments yet' : 'No matching courses'}
          description={enrollments.length === 0 ? 'Browse the catalog to find courses and start learning.' : 'Try adjusting your search or filters.'}
          action={enrollments.length === 0 ? <AppButton onClick={() => navigate('/s/self-paced')}>Browse Courses</AppButton> : undefined}
        />
      ) : (
        <div className="spe-grid">
          {filtered.map(e => {
            const courseId = e.course?._id || e.course;
            return (
              <EnrollmentCard
                key={e._id || courseId}
                enrollment={e}
                onClick={() => navigate(`/s/self-paced/${courseId}`, { state: { from: 'enrollments' } })}
                onViewCert={(cId) => navigate('/s/self-paced/certificates')}
                onRepurchase={(course) => navigate(`/s/self-paced/${course.slug || course._id}`, { state: { from: 'enrollments' } })}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
